<template>
  <!--
    O QUADRO DO IA4COM — o iframe e a ponte com ele (TEL-2327).

    Saiu de IA4COM/Index.vue para a pagina `/assistant` e o painel lateral do
    relatorio de chamadas usarem a MESMA ponte: termos antes do quadro, as tres
    conferencias de mensagem, identidade, token e veu. Duas copias disto
    divergiriam no primeiro ajuste, e a que ficasse para tras vazaria token ou
    mostraria o assistente a quem nao aceitou os termos.

    Quem usa decide o TAMANHO (a raiz ocupa 100% da altura que recebe) e o
    ROLLOUT (este componente so nasce para quem ja foi liberado).
  -->
  <div class="ia4com-quadro">
    <!--
      O ACEITE DOS TERMOS VEM ANTES DO ASSISTENTE (TEL-2272).

      O iframe so nasce depois do aceite: se nascesse antes e o pedido so cobrisse
      por cima, o token de sessao ja teria viajado para dentro dele. Cobrir com
      CSS nao e controle de acesso. Enquanto a consulta nao volta, a espera.
    -->
    <div
      v-if="termsPending === null"
      class="ia4com-carregando ia4com-carregando--bloco">
      <img
        src="/static/images/loading.gif"
        width="28"
        height="28"
        alt=""
        aria-hidden="true" />
      <span>Carregando o assistente…</span>
    </div>

    <div v-else-if="termsPending === true" class="ia4com-quadro__termos">
      <TermsAcceptanceModal
        :pendingTerms="pendingTerms"
        @aceito="onTermsAccepted" />
    </div>

    <div v-else class="card ia4com-card">
      <!--
        O VEU SOBRE O QUADRO. Quem diz que acabou e o proprio app, pelo
        `ia4com:ready` — nao o `load` do iframe, que responde "o documento
        chegou" antes de o Vue de la ter desenhado.
      -->
      <transition name="ia4com-veu">
        <div v-if="!quadroPronto" class="ia4com-carregando ia4com-carregando--quadro">
          <img
            src="/static/images/loading.gif"
            width="28"
            height="28"
            alt=""
            aria-hidden="true" />
          <span>Abrindo suas conversas…</span>
        </div>
      </transition>

      <!--
        `allow="clipboard-write; microphone"`: o que nao estiver nesta lista o
        navegador recusa la dentro antes de perguntar qualquer coisa — o
        `microphone` e o ditado do IA4COM. Explicito mesmo em mesma origem, porque
        no desenvolvimento o `baseUrl` aponta para outra porta, e ai o iframe e de
        outra origem.
      -->
      <iframe
        ref="frame"
        class="ia4com-frame"
        :src="frameSrc"
        title="IA4COM — assistente de vendas"
        allow="clipboard-write; microphone" />
    </div>
  </div>
</template>

<script>
/**
 * O protocolo esta documentado dos dois lados. Mexeu aqui, mexa em
 * `app/composables/useEmbed.ts` do ia4com-app:
 *
 *   iframe -> nos    : ia4com:ready      montei, pode mandar
 *   nos    -> iframe : ia4com:identity   quem e a conta, o vendedor e o token
 *   nos    -> iframe : ia4com:ligacao    a ligacao escolhida no relatorio (TEL-2327)
 *   nos    -> iframe : ia4com:visibilidade  o painel escondeu ou mostrou o quadro
 */
import { mapState } from 'vuex'
import { fetchPendingTerms } from '../../services/terms'
import TermsAcceptanceModal from './TermsAcceptanceModal.vue'

/** marca deste host nas mensagens — o IA4COM so aceita as que trazem isto */
const HOST_SOURCE = 'api4com-portal'

/** passado este tempo sem `ia4com:ready`, o veu sai de qualquer forma */
const TETO_DO_VEU_MS = 10000

export default {
  name: 'QuadroIA4COM',

  components: { TermsAcceptanceModal },

  props: {
    /** embute no modo painel (estreito) do IA4COM — o do relatorio de chamadas */
    painel: { type: Boolean, default: false },
    /** a ligacao a abrir no assistente, quando houver */
    callId: { type: String, default: null },
    /**
     * Muda a cada clique. Clicar de novo na MESMA ligacao precisa reabrir, e um
     * `watch` so no `callId` nao dispararia.
     */
    pedidoEm: { type: Number, default: 0 },
    /**
     * Falso quando quem usa ESCONDE o quadro sem destrui-lo (o painel fechado).
     * `display: none` nao chega ao iframe como `visibilitychange`: sem o aviso, um
     * ditado em curso seguiria gravando com o painel fechado.
     */
    visivel: { type: Boolean, default: true }
  },

  data () {
    return {
      /**
       * De onde o app e servido. Em producao, caminho do proprio dominio
       * (`/ia4com/`): mesma origem, e o `targetOrigin` do postMessage vira exato.
       */
      baseUrl: process.env.IA4COM_URL || '/ia4com/',
      /** null enquanto a consulta de termos nao respondeu (TEL-2272) */
      termsPending: null,
      pendingTerms: [],
      /**
       * O veu ja saiu. Sai pelo `ia4com:ready` OU pelo cronometro de 10 s — e por
       * isso nao serve para saber se o app escuta (ver `appOuvindo`).
       */
      quadroPronto: false,
      /**
       * O app respondeu `ia4com:ready`: so a partir daqui uma mensagem tem quem a
       * receba. Com rede lenta o veu saia por tempo antes disso, e uma ligacao
       * clicada nesse intervalo era enviada para ninguem e tirada da fila.
       */
      appOuvindo: false,
      timerDoVeu: null,
      /**
       * A LIGACAO NA FILA. `postMessage` nao tem fila: mandada antes do
       * `ia4com:ready`, ela se perderia. Guardada aqui, sai logo depois da
       * identidade.
       */
      ligacaoPendente: null
    }
  },

  computed: {
    // `mapState`, nao `mapGetters`: o modulo `auth` nao tem getter nenhum
    ...mapState('auth', ['user', 'organization']),

    frameSrc () {
      return `${this.baseUrl}?embed=portal${this.painel ? '&painel=1' : ''}`
    },

    /** origem do iframe, para o `targetOrigin` do postMessage */
    frameOrigin () {
      if (this.baseUrl.charAt(0) === '/') return window.location.origin
      return new URL(this.baseUrl, window.location.href).origin
    },

    /**
     * UM VALOR SO PARA `callId` E `pedidoEm`. Quem usa muda os dois no mesmo
     * clique; com um watcher para cada, os dois rodavam no mesmo ciclo e a ligacao
     * saia DUAS vezes — duas conversas no IA4COM e o dobro das cotas.
     */
    pedido () {
      return `${this.callId || ''}@${this.pedidoEm}`
    }
  },

  watch: {
    pedido () {
      this.pedirLigacao()
    },
    visivel () {
      this.enviarVisibilidade()
    }
  },

  created () {
    this.checkTerms()
    this.pedirLigacao()
  },

  mounted () {
    window.addEventListener('message', this.onFrameMessage)
  },

  beforeDestroy () {
    window.removeEventListener('message', this.onFrameMessage)
    // sair antes de o quadro abrir nao pode deixar timer mexendo num componente morto
    if (this.timerDoVeu) clearTimeout(this.timerDoVeu)
  },

  methods: {
    /**
     * Pergunta o que falta aceitar. Falha de rede devolve lista vazia
     * (`services/terms.js`) e a tela segue sem pendencia, de proposito: um
     * bloqueio levantado porque o backend piscou tira do ar a tela inteira.
     */
    checkTerms () {
      return fetchPendingTerms().then(pendingTerms => {
        this.pendingTerms = pendingTerms
        this.termsPending = pendingTerms.length > 0
        if (!this.termsPending) this.armarVeu()
      })
    },

    onTermsAccepted () {
      this.pendingTerms = []
      this.termsPending = false
      this.armarVeu()
    },

    /** o cronometro de seguranca do veu comeca com o quadro nascendo */
    armarVeu () {
      if (this.timerDoVeu) clearTimeout(this.timerDoVeu)
      this.timerDoVeu = setTimeout(this.descobrirQuadro, TETO_DO_VEU_MS)
    },

    /**
     * O IA4COM avisa que montou e so entao mandamos identidade e ligacao.
     * Mandar antes seria mandar no vazio.
     */
    onFrameMessage (event) {
      if (event.origin !== this.frameOrigin) return
      // a origem diz o DOMINIO; isto diz que veio do NOSSO quadro, nao de um irmao
      const frame = this.$refs.frame
      if (!frame || event.source !== frame.contentWindow) return
      const data = event.data
      if (!data || data.source !== 'ia4com-app' || data.type !== 'ia4com:ready') return

      this.descobrirQuadro()
      this.appOuvindo = true
      this.enviarIdentidade()
      this.enviarLigacao()
      // painel fechado antes de o app ficar pronto: o app nasce achando que esta visivel
      if (!this.visivel) this.enviarVisibilidade()
    },

    /** tira o veu uma vez so, limpando o cronometro de quem chegou por ultimo */
    descobrirQuadro () {
      if (this.timerDoVeu) {
        clearTimeout(this.timerDoVeu)
        this.timerDoVeu = null
      }
      this.quadroPronto = true
    },

    enviarIdentidade () {
      const frame = this.$refs.frame
      if (!frame || !frame.contentWindow) return

      const user = this.user || {}
      const org = this.organization || {}

      // os campos vao dentro de `payload` — o formato que `useEmbed.ts` le
      frame.contentWindow.postMessage({
        source: HOST_SOURCE,
        type: 'ia4com:identity',
        payload: {
          conta: org.uuid || org.id || org._id || user.organizationId || '',
          contaNome: org.name || '',
          vendedor: user.email || '',
          vendedorNome: user.name || '',
          ramal: user.extension || '',
          versaoWebphone: '',
          portalHistoryUrl: '',
          // o IA4COM troca por sessao propria e NAO o guarda; targetOrigin exato
          token: this.lerToken()
        }
      }, this.frameOrigin)
    },

    /** poe a ligacao atual na fila e manda, se o app ja estiver ouvindo */
    pedirLigacao () {
      if (!this.callId) return
      this.ligacaoPendente = this.callId
      if (this.appOuvindo) this.enviarLigacao()
    },

    /**
     * So o id da ligacao atravessa. Quem decide se esta pessoa pode abri-la e o
     * pbxapi, com o token da sessao; o IA4COM trata o id como declaracao.
     */
    enviarLigacao () {
      const frame = this.$refs.frame
      if (!this.ligacaoPendente || !frame || !frame.contentWindow) return
      frame.contentWindow.postMessage({
        source: HOST_SOURCE,
        type: 'ia4com:ligacao',
        payload: { callId: this.ligacaoPendente }
      }, this.frameOrigin)
      this.ligacaoPendente = null
    },

    /** so depois do `ready`: antes disso nao ha app ouvindo, e nao ha ditado a soltar */
    enviarVisibilidade () {
      const frame = this.$refs.frame
      if (!this.appOuvindo || !frame || !frame.contentWindow) return
      frame.contentWindow.postMessage({
        source: HOST_SOURCE,
        type: 'ia4com:visibilidade',
        payload: { visivel: this.visivel }
      }, this.frameOrigin)
    },

    /** o mesmo item de localStorage que src/services/loopback.js escreve */
    lerToken () {
      try {
        const bruto = window.localStorage.getItem('loopback-token')
        if (!bruto) return ''
        const token = JSON.parse(bruto)
        return (token && (token.id || token)) || ''
      } catch (e) {
        return ''
      }
    }
  }
}
</script>

<style scoped>
/* a raiz ocupa a altura que quem usa der — a pagina mede, o painel estica */
.ia4com-quadro {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/*
 * O .card do tema traz margin-bottom e uma transicao de 0.5s em tudo; aqui ha um
 * card so e a altura muda a cada resize. O overflow recorta o iframe no raio.
 */
.ia4com-card {
  flex: 1;
  min-height: 0;
  margin-bottom: 0;
  overflow: hidden;
  transition: none;
  position: relative;
}

.ia4com-frame {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
}

/* no painel estreito, o pedido de aceite precisa rolar em vez de impor altura de pagina */
.ia4com-quadro__termos {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.ia4com-quadro__termos >>> .terms-area {
  min-height: 100%;
}

/* o sinal de carga: o mesmo dos Relatorios (loading.gif de 28px + uma frase) */
.ia4com-carregando {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #6b7280;
  font-size: 0.875rem;
}

.ia4com-carregando--bloco {
  flex: 1;
  min-height: 0;
}

.ia4com-carregando--quadro {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  background: #ffffff;
}

/* so a saida tem transicao; o veu nasce junto com o card */
.ia4com-veu-leave-active {
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.ia4com-veu-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .ia4com-veu-leave-active {
    transition: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/IA4COM/QuadroIA4COM.vue